import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import './Settings.css';

const Settings = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    
    // Cloudinary configuration
    const CLOUDINARY_CLOUD_NAME = 'dblffn98e';
    const CLOUDINARY_UPLOAD_PRESET = 'WHITE DOVES MINISTRY';
    
    // State for form data
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        role: '',
        department: '',
        isDepartmentLeader: ''
    });
    
    // State for password form
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    
    // State for UI
    const [loading, setLoading] = useState({
        profile: false,
        password: false,
        info: false,
        delete: false
    });
    
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [hasProfilePhoto, setHasProfilePhoto] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [userCollection, setUserCollection] = useState('users'); // 'users' or 'admins'

    // Load user data on component mount
    useEffect(() => {
        if (userData && currentUser) {
            // Determine which collection the user belongs to
            checkUserCollection();
            
            setFormData({
                fullName: userData.fullName || userData.displayName || '',
                email: currentUser?.email || '',
                phone: userData.phone || '',
                role: userData.role || '',
                department: userData.department || '',
                isDepartmentLeader: userData.isDepartmentLeader || ''
            });
            
            // Check if user has profile photo
            if (userData.photoURL || userData.profilePhoto) {
                setHasProfilePhoto(true);
                setProfileImagePreview(userData.photoURL || userData.profilePhoto);
                setShowAlert(false);
            } else {
                setShowAlert(true);
                setAlertMessage('Please upload a profile photo to complete your profile.');
            }
        }
    }, [userData, currentUser]);

    // Check which collection the user is in
    const checkUserCollection = async () => {
        try {
            // Check if user exists in admins collection
            const adminDoc = await getDoc(doc(db, 'admins', currentUser.uid));
            if (adminDoc.exists()) {
                setUserCollection('admins');
            } else {
                setUserCollection('users');
            }
        } catch (error) {
            console.error('Error checking user collection:', error);
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handlePasswordChange = (e) => {
        const { id, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    // Handle profile photo selection
    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showToast('Please select an image file (JPG, PNG, WebP)', 'error');
                return;
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image size must be less than 5MB', 'error');
                return;
            }

            setProfileImage(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload to Cloudinary
    const uploadToCloudinary = async (file) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const percent = Math.round((e.loaded / e.total) * 100);
                    setUploadProgress(percent);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    resolve({
                        url: data.secure_url,
                        publicId: data.public_id,
                        width: data.width,
                        height: data.height,
                        format: data.format
                    });
                } else {
                    reject(new Error('Upload failed'));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error'));
            });

            xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);
            xhr.send(formData);
        });
    };

    // Handle photo upload
    const handleUploadPhoto = async () => {
        if (!profileImage) {
            showToast('Please select a photo first', 'error');
            return;
        }

        setLoading(prev => ({ ...prev, profile: true }));
        setUploadProgress(0);

        try {
            // Upload to Cloudinary
            const result = await uploadToCloudinary(profileImage);

            // Update user document in appropriate Firestore collection
            const userRef = doc(db, userCollection, currentUser.uid);
            await updateDoc(userRef, {
                photoURL: result.url,
                photoPublicId: result.publicId,
                photoDetails: {
                    width: result.width,
                    height: result.height,
                    format: result.format,
                    uploadedAt: new Date().toISOString()
                },
                updatedAt: new Date().toISOString()
            });

            // Update local state
            setHasProfilePhoto(true);
            setProfileImagePreview(result.url);
            setProfileImage(null);
            setShowAlert(false);
            
            showToast('Profile photo uploaded successfully!', 'success');
            setUploadProgress(0);
            
            // Clear file input
            document.getElementById('profilePhotoUpload').value = '';
            
        } catch (error) {
            console.error('Error uploading photo:', error);
            showToast('Failed to upload photo. Please try again.', 'error');
        } finally {
            setLoading(prev => ({ ...prev, profile: false }));
        }
    };

    // Handle photo removal
    const handleRemovePhoto = async () => {
        if (!window.confirm('Are you sure you want to remove your profile photo?')) {
            return;
        }

        setLoading(prev => ({ ...prev, profile: true }));

        try {
            // Update user document in appropriate Firestore collection
            const userRef = doc(db, userCollection, currentUser.uid);
            
            await updateDoc(userRef, {
                photoURL: null,
                photoPublicId: null,
                photoDetails: null,
                updatedAt: new Date().toISOString()
            });

            // Update local state
            setHasProfilePhoto(false);
            setProfileImagePreview(null);
            setShowAlert(true);
            setAlertMessage('Please upload a profile photo to complete your profile.');
            
            showToast('Profile photo removed', 'info');
        } catch (error) {
            console.error('Error removing photo:', error);
            showToast('Failed to remove photo. Please try again.', 'error');
        } finally {
            setLoading(prev => ({ ...prev, profile: false }));
        }
    };

    // Handle password change
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        // Validate passwords
        if (passwordData.newPassword.length < 6) {
            showToast('New password must be at least 6 characters long', 'error');
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            showToast('New passwords do not match', 'error');
            return;
        }

        setLoading(prev => ({ ...prev, password: true }));

        try {
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(
                currentUser.email,
                passwordData.currentPassword
            );
            
            await reauthenticateWithCredential(currentUser, credential);
            
            // Update password
            await updatePassword(currentUser, passwordData.newPassword);
            
            // Clear form
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            
            showToast('Password updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating password:', error);
            
            let errorMessage = 'Failed to update password.';
            if (error.code === 'auth/wrong-password') {
                errorMessage = 'Current password is incorrect';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'New password is too weak';
            } else if (error.code === 'auth/requires-recent-login') {
                errorMessage = 'Please log out and log in again before changing your password';
            }
            
            showToast(errorMessage, 'error');
        } finally {
            setLoading(prev => ({ ...prev, password: false }));
        }
    };

    // Handle account info update
    const handleInfoSubmit = async (e) => {
        e.preventDefault();

        setLoading(prev => ({ ...prev, info: true }));

        try {
            // Update the appropriate collection based on user type
            const userRef = doc(db, userCollection, currentUser.uid);
            
            const updateData = {
                fullName: formData.fullName,
                phone: formData.phone,
                updatedAt: new Date().toISOString()
            };

            // Regular users can only update basic info
            // Admin-specific fields (role, department, isDepartmentLeader) are NOT included in the update
            // as they are read-only and can only be changed by super admin via Firestore console

            await updateDoc(userRef, updateData);

            showToast('Account information updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating account info:', error);
            showToast('Failed to update account information', 'error');
        } finally {
            setLoading(prev => ({ ...prev, info: false }));
        }
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            return;
        }

        setLoading(prev => ({ ...prev, delete: true }));

        try {
            // Update user status in appropriate Firestore collection
            const userRef = doc(db, userCollection, currentUser.uid);
            await updateDoc(userRef, {
                status: 'deleted',
                deletedAt: new Date().toISOString()
            });

            // Delete user from Firebase Auth
            await currentUser.delete();

            showToast('Account deleted successfully', 'success');
            
            // Redirect to landing page
            window.location.href = '/';
        } catch (error) {
            console.error('Error deleting account:', error);
            
            if (error.code === 'auth/requires-recent-login') {
                showToast('Please log out and log in again before deleting your account', 'error');
            } else {
                showToast('Failed to delete account. Please try again.', 'error');
            }
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
        }
    };

    if (!userData) {
        return (
            <div className="settings-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading settings...</p>
            </div>
        );
    }

    return (
        <div className="settings-page">
            <h1 className="settings-page-title">Account Settings</h1>

            {/* Profile Photo Alert - Only shows if user has no photo */}
            {showAlert && (
                <div className="settings-alert settings-alert-info">
                    <i className="fas fa-info-circle"></i>
                    <span>{alertMessage}</span>
                </div>
            )}

            {/* Profile Info */}
            <div className="settings-profile-info">
                <div className="settings-profile-pic-container">
                    <div className="settings-profile-pic">
                        {profileImagePreview ? (
                            <img 
                                src={profileImagePreview} 
                                alt="Profile" 
                                className="settings-profile-img"
                            />
                        ) : (
                            <i className="fas fa-user"></i>
                        )}
                        <div className="settings-profile-pic-edit">Change Photo</div>
                        <input
                            type="file"
                            id="profilePhotoUpload"
                            className="settings-profile-pic-upload"
                            accept="image/*"
                            onChange={handlePhotoSelect}
                        />
                    </div>
                    <div className="settings-profile-details">
                        <h3>{formData.fullName || 'User'}</h3>
                        <p>{formData.email}</p>
                        {isAdmin && (
                            <>
                                <p className="settings-profile-role">Administrator</p>
                                {formData.role && (
                                    <p className="settings-profile-role">Role: {formData.role}</p>
                                )}
                                {formData.department && (
                                    <p className="settings-profile-role">Department: {formData.department}</p>
                                )}
                                {formData.isDepartmentLeader && (
                                    <p className="settings-profile-role">Department Leader: {formData.isDepartmentLeader}</p>
                                )}
                            </>
                        )}
                        <p className="settings-profile-collection">
                            Account Type: {userCollection === 'admins' ? 'Administrator' : 'Member'}
                        </p>
                    </div>
                </div>

                {/* Photo upload actions */}
                <div className="settings-photo-actions">
                    <div className="settings-photo-upload">
                        <button
                            className="settings-btn settings-btn-secondary"
                            onClick={handleUploadPhoto}
                            disabled={!profileImage || loading.profile}
                        >
                            {loading.profile ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    {uploadProgress > 0 ? `Uploading ${uploadProgress}%...` : 'Uploading...'}
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload"></i>
                                    Upload New Photo
                                </>
                            )}
                        </button>
                        <div className="settings-photo-requirements">
                            JPG, PNG, WebP (max 5MB)
                        </div>
                    </div>
                    {hasProfilePhoto && (
                        <button
                            className="settings-btn settings-btn-danger"
                            onClick={handleRemovePhoto}
                            disabled={loading.profile}
                        >
                            {loading.profile ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Removing...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-trash"></i>
                                    Remove Photo
                                </>
                            )}
                        </button>
                    )}
                </div>

                {/* Upload Progress Bar */}
                {loading.profile && uploadProgress > 0 && (
                    <div className="settings-upload-progress">
                        <div className="settings-progress-bar">
                            <div 
                                className="settings-progress-fill" 
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                        <div className="settings-progress-text">
                            {uploadProgress}% uploaded
                        </div>
                    </div>
                )}
            </div>

            {/* Account Information Section */}
            <div className="settings-section">
                <h2 className="settings-section-title">Account Information</h2>
                <form onSubmit={handleInfoSubmit} className="settings-form">
                    <div className="settings-form-group">
                        <label htmlFor="fullName">Full Name</label>
                        <input
                            type="text"
                            id="fullName"
                            className="settings-form-control"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="settings-form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            className="settings-form-control"
                            value={formData.email}
                            readOnly
                            disabled
                        />
                        <small className="settings-form-hint">Email cannot be changed</small>
                    </div>
                    <div className="settings-form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                            type="tel"
                            id="phone"
                            className="settings-form-control"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="254XXXXXXXXX"
                        />
                    </div>

                    {/* Admin-only fields - READ ONLY - displayed for information only */}
                    {userCollection === 'admins' && (
                        <>
                            <div className="settings-form-group">
                                <label htmlFor="role">Role</label>
                                <input
                                    type="text"
                                    id="role"
                                    className="settings-form-control settings-form-control-readonly"
                                    value={formData.role || 'Not assigned'}
                                    readOnly
                                    disabled
                                />
                                <small className="settings-form-hint">Role can only be changed by Super Admin</small>
                            </div>

                            <div className="settings-form-group">
                                <label htmlFor="department">Department</label>
                                <input
                                    type="text"
                                    id="department"
                                    className="settings-form-control settings-form-control-readonly"
                                    value={formData.department || 'Not assigned'}
                                    readOnly
                                    disabled
                                />
                                <small className="settings-form-hint">Department can only be changed by Super Admin</small>
                            </div>

                            <div className="settings-form-group">
                                <label htmlFor="isDepartmentLeader">Department Leader</label>
                                <input
                                    type="text"
                                    id="isDepartmentLeader"
                                    className="settings-form-control settings-form-control-readonly"
                                    value={formData.isDepartmentLeader || 'No'}
                                    readOnly
                                    disabled
                                />
                                <small className="settings-form-hint">Leader status can only be changed by Super Admin</small>
                            </div>
                        </>
                    )}

                    <button
                        type="submit"
                        className="settings-btn settings-btn-primary"
                        disabled={loading.info}
                    >
                        {loading.info ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Updating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i>
                                Update Information
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* Change Password Section */}
            <div className="settings-section">
                <h2 className="settings-section-title">Change Password</h2>
                <form onSubmit={handlePasswordSubmit} className="settings-form">
                    <div className="settings-form-group">
                        <label htmlFor="currentPassword">Current Password</label>
                        <input
                            type="password"
                            id="currentPassword"
                            className="settings-form-control"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            required
                        />
                    </div>
                    <div className="settings-form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                            type="password"
                            id="newPassword"
                            className="settings-form-control"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            minLength="6"
                            required
                        />
                    </div>
                    <div className="settings-form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            className="settings-form-control"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            minLength="6"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="settings-btn settings-btn-primary"
                        disabled={loading.password}
                    >
                        {loading.password ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Updating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-key"></i>
                                Update Password
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* Danger Zone */}
            <div className="settings-section settings-danger-zone">
                <h2 className="settings-section-title">Danger Zone</h2>
                <div className="settings-form-group">
                    <p className="settings-danger-message">
                        Deleting your account will remove all your data from our system. 
                        This action cannot be undone.
                    </p>
                    <button
                        className="settings-btn settings-btn-danger"
                        onClick={handleDeleteAccount}
                        disabled={loading.delete}
                    >
                        {loading.delete ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-exclamation-triangle"></i>
                                Delete My Account
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Settings;
